import { Spinner } from "../../../components/Spinner";
import {
  useBuscarTramiteMutation,
  useGetBusquedaQuery,
} from "../../../services/docs";
import { useForm } from "../../../hooks/useForm";
import { useNavigate } from "react-router-dom";
import { toastr } from "react-redux-toastr";
import { TablaTramitesComponent } from "../../../components/TablaTramitesComponent";
import React from "react";

export const BuscarScreen = () => {
  const navigate = useNavigate();
  const { data, isLoading } = useGetBusquedaQuery();

  const { formulario, handleInputChange, handleSelectChange } = useForm({
    fi_busqueda_id: "",
    fc_valor: "",
  });

  const [
    buscarTramiteAction,
    { data: siniestros, isLoading: loadingBusqueda },
  ] = useBuscarTramiteMutation();

  // Acción para ver el detalle
  const verMas = (p: any) => {
    navigate(`/view/siniestro/${p.t_fc_siniestro}/${p.t_id}/proceso`);
  };

  // Acción de Búsqueda
  const buscarTramite = async (e?: React.FormEvent) => {
    if (e) e.preventDefault(); // Permite buscar con Enter

    if (!formulario.fi_busqueda_id || +formulario.fi_busqueda_id === 0) {
      toastr.error("Debe seleccionar un tipo de búsqueda");
      return;
    }
    if (!formulario.fc_valor || formulario.fc_valor.trim() === "") {
      toastr.error("Debes agregar un valor de búsqueda");
      return;
    }

    try {
      await buscarTramiteAction({
        ...formulario,
        fi_busqueda_id: +formulario.fi_busqueda_id,
      }).unwrap();
    } catch (err: any) {
      if (err?.status === 400 && err?.data?.message) {
        if (Array.isArray(err.data.message)) {
          err.data.message.forEach((m: string) => toastr.error(m));
        } else {
          toastr.error(err.data.message);
        }
      } else {
        toastr.error("Ocurrió un error al realizar la búsqueda");
      }
    }
  };

  // Loading principal de las opciones del Select
  if (isLoading) {
    return (
      <div className="flex h-64 w-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* --- Contenedor de Búsqueda --- */}
      <section className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">

        {/* Cabecera */}
        <div className="flex flex-col gap-2 border-b border-slate-100 px-7 py-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-xl font-bold tracking-tight text-slate-900">Buscar Siniestro</h1>
            <p className="mt-0.5 text-sm text-slate-500">
              Localice siniestros y trámites registrados utilizando diferentes criterios de búsqueda.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
              <svg className="h-3.5 w-3.5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              Módulo de Consultas
            </span>
          </div>
        </div>

        {/* Subtítulo de Sección */}
        <div className="px-7 pb-2 pt-6">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
            <svg className="h-4 w-4 text-brand-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
            </svg>
            <h2>Criterios de Búsqueda</h2>
          </div>
        </div>

        {/* Formulario */}
        <form onSubmit={buscarTramite} className="px-7 pb-7 pt-3">
          <div className="grid grid-cols-1 items-end gap-5 lg:grid-cols-12">

            {/* Select: Tipo Búsqueda */}
            <div className="lg:col-span-4">
              <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-600" htmlFor="fi_busqueda_id">
                Tipo Búsqueda
              </label>
              <div className="relative">
                <select
                  id="fi_busqueda_id"
                  name="fi_busqueda_id"
                  onChange={handleSelectChange}
                  value={formulario.fi_busqueda_id}
                  className="h-11 w-full cursor-pointer rounded-xl border border-slate-200 bg-slate-50/50 pl-3.5 pr-10 text-sm font-medium text-slate-800 transition-all hover:bg-slate-50 focus:border-brand-500 focus:bg-white focus:ring-2 focus:ring-brand-500/20"
                >
                  <option disabled value="">Selecciona criterio...</option>
                  {data?.busqueda?.map((b: any) => (
                    <option key={b.id} value={b.id}>
                      {b.fc_nombre_busqueda}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Input: Valor a buscar */}
            <div className="lg:col-span-5">
              <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-600" htmlFor="fc_valor">
                Valor a buscar:
              </label>
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-slate-400">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"></path>
                  </svg>
                </div>
                <input
                  id="fc_valor"
                  name="fc_valor"
                  type="text"
                  onChange={handleInputChange}
                  value={formulario.fc_valor}
                  placeholder="Ingrese el valor (ej. número de folio)..."
                  className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50/50 pl-10 pr-4 text-sm font-medium text-slate-900 transition-all hover:bg-slate-50 placeholder:text-slate-400 focus:border-brand-500 focus:bg-white focus:ring-2 focus:ring-brand-500/20"
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2.5 lg:col-span-3">
              <button
                type="submit"
                disabled={loadingBusqueda}
                className="flex h-11 flex-1 items-center justify-center gap-2 rounded-xl bg-brand-500 px-5 text-sm font-semibold text-white shadow-sm shadow-brand-500/25 transition-all hover:-translate-y-0.5 hover:bg-brand-600 hover:shadow active:bg-brand-700 disabled:opacity-70"
              >
                {loadingBusqueda ? (
                  <Spinner />
                ) : (
                  <>
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                    </svg>
                    <span>Buscar</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </section>

      {/* --- Área de Resultados --- */}
      <div className="space-y-4">

        {/* Loading State visual al buscar */}
        {loadingBusqueda && (
          <div className="flex w-full items-center justify-center py-12">
            <div className="flex flex-col items-center gap-3 text-slate-500">
              <Spinner />
              <span className="text-sm font-medium">Buscando siniestros en el sistema...</span>
            </div>
          </div>
        )}

        {/* Tabla de Resultados */}
        {!loadingBusqueda && siniestros?.busquedas && siniestros.busquedas.length > 0 && (
          <section className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-slate-100 px-7 py-4">
              <div className="flex items-center gap-2.5">
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-500"></span>
                <h3 className="text-sm font-bold text-slate-900">Resultados Encontrados</h3>
                <span className="text-xs font-medium text-slate-400">({siniestros.busquedas.length} registros)</span>
              </div>
            </div>

            <div className="p-6">
              <TablaTramitesComponent
                data={siniestros.busquedas}
                texto_accion="Ver proceso"
                accion={verMas}
                ejecutivos={[]}
                cargando={false}
              />
            </div>
          </section>
        )}

        {/* Estado Vacío (Sin Resultados) */}
        {!loadingBusqueda && siniestros?.busquedas && siniestros.busquedas.length === 0 && (
          <div className="flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">
            <svg className="h-5 w-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span className="text-sm font-semibold">No se encontraron siniestros que coincidan con los criterios de búsqueda.</span>
          </div>
        )}
      </div>
    </div>
  );
};
